// ============================================================
// Pay periods
//
// Derived from the payroll schedule: 14-day periods starting Monday, paid the
// Friday five days after the period ends.
//
//   2026-04-13 → 2026-04-26, paid 2026-05-01
//   2026-04-27 → 2026-05-10, paid 2026-05-15
//
// Everything is generated from one anchor date, so if payroll ever shifts,
// change ANCHOR and the whole schedule moves with it.
// ============================================================

export const PAY_ANCHOR = '2026-04-13'   // a Monday, start of a known period
export const PERIOD_DAYS = 14
export const PAY_OFFSET_DAYS = 5         // days after period end that pay lands

const MS = 86400000
const toDate = s => new Date(String(s).slice(0, 10) + 'T12:00:00')
const toISO = d => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
const addDays = (d, n) => new Date(d.getTime() + n * MS)

// Which period number contains this date? Negative for dates before the anchor,
// which is fine — the maths works in both directions.
export const periodIndexFor = (dateStr) => {
  const diff = Math.floor((toDate(dateStr) - toDate(PAY_ANCHOR)) / MS)
  return Math.floor(diff / PERIOD_DAYS)
}

export const periodByIndex = (index) => {
  const start = addDays(toDate(PAY_ANCHOR), index * PERIOD_DAYS)
  const end = addDays(start, PERIOD_DAYS - 1)
  const pay = addDays(end, PAY_OFFSET_DAYS)
  return {
    index,
    start: toISO(start),
    end: toISO(end),
    payDate: toISO(pay),
  }
}

export const currentPeriod = () => periodByIndex(periodIndexFor(toISO(new Date())))

// The most recent `count` periods, newest first, including the one in progress.
export const recentPeriods = (count = 6) => {
  const now = periodIndexFor(toISO(new Date()))
  return Array.from({ length: count }, (_, i) => periodByIndex(now - i))
}

export const isCurrentPeriod = (p) => p.index === periodIndexFor(toISO(new Date()))

// A period that has ended but hasn't been paid yet — worth showing differently,
// since the hours are final but the money hasn't landed.
export const periodState = (p) => {
  const today = toISO(new Date())
  if (today <= p.end) return 'open'
  if (today < p.payDate) return 'pending'
  return 'paid'
}

export const PERIOD_STATE = {
  open:    { label: 'In progress', color: 'var(--gold)',       bg: '#fff8e1' },
  pending: { label: 'Awaiting pay', color: 'var(--navy-mid)',  bg: '#eef2fa' },
  paid:    { label: 'Paid',         color: 'var(--text-muted)', bg: 'transparent' },
}

// Buckets used in the payroll sheet. `other` catches technician and
// supervising_ad so hours are never silently dropped from the total.
export const PAY_BUCKETS = [
  { key: 'am', label: 'AM', color: '#8c4d78' },
  { key: 'ad', label: 'AD', color: '#2a6e84' },
]

export const bucketFor = (payLevel) =>
  payLevel === 'am' ? 'am' : payLevel === 'ad' ? 'ad' : 'other'

// Totals per pay level for a set of entries, in seconds.
export const totalsByBucket = (entries = []) => {
  const out = { am: 0, ad: 0, other: 0, total: 0 }
  entries.forEach(e => {
    const secs = e.duration_seconds || 0
    out[bucketFor(e.pay_level)] += secs
    out.total += secs
  })
  return out
}

// Decimal hours, the way a payroll sheet shows them (71.42 not 71:25).
export const decimalHours = (seconds) => (seconds || 0) / 3600

export const fmtHours = (seconds) => decimalHours(seconds).toFixed(2)

export const fmtRange = (p) => {
  const f = s => {
    const d = toDate(s)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  return `${f(p.start)} – ${f(p.end)}`
}

export const fmtDay = (s) => {
  if (!s) return ''
  return toDate(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Does an entry fall inside a period?
export const inPeriod = (entryDate, p) =>
  entryDate >= p.start && entryDate <= p.end
